import { useState } from 'react';

import { makeStyles } from '@material-ui/core/styles';

import { RiShieldCheckLine } from '@remixicon/react';
import classNames from 'classnames';

import { Button, Text } from '@backstage/ui';

import { ConversationsV2ToolConfirmationResponseDecision } from '@spotify/confidence-graphql';

import { useToolExtensions } from '../../extensions';
import { useConfirmTool } from '../../hooks/useConfirmTool';
import { ChatInformationBox } from '../ChatInformationBox';
import { formatDefaultToolLabel } from '../ToolCallGroup/defaultToolMappings';
import { ToolInputPreview } from './ToolInputPreview';

const useStyles = makeStyles({
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--bui-space-2)',
    marginTop: 'var(--bui-space-2)',
  },
  buttons: {
    display: 'flex',
    gap: 'var(--bui-space-2)',
  },
  iconApproved: {
    color: 'var(--bui-fg-success)',
  },
  iconRejected: {
    color: 'var(--bui-fg-danger)',
  },
});

export const ToolConfirmationRequestMessage = ({
  eventName,
  tool,
  displayName,
  input,
}: {
  eventName: string;
  tool: string;
  displayName?: string | null;
  input?: unknown;
}) => {
  const classes = useStyles();
  const toolExtensions = useToolExtensions();
  const extension = toolExtensions.find(e => e.predicate(tool));
  const entry = { tool, displayName, input };
  const renderedLabel = extension?.renderLabel?.(entry);
  const label =
    !renderedLabel && extension?.formatLabel
      ? extension.formatLabel(entry)
      : formatDefaultToolLabel(entry);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [pendingDecision, setPendingDecision] =
    useState<ConversationsV2ToolConfirmationResponseDecision | null>(null);
  const { confirmTool, isMutating, error } = useConfirmTool({
    onComplete: () => setHasSubmitted(true),
  });

  const handleDecide = (
    decision: ConversationsV2ToolConfirmationResponseDecision,
  ) => {
    setPendingDecision(decision);
    void confirmTool(eventName, decision).catch(() => {
      setPendingDecision(null);
      // Error surfaces via the hook's `error` state; no additional handling needed.
    });
  };

  const actionsDisabled = isMutating || hasSubmitted;

  return (
    <>
      <ChatInformationBox
        icon={<RiShieldCheckLine size={16} />}
        title={
          renderedLabel || (
            <Text variant="body-small">
              Wants to call <strong>{label.text}</strong>
              {label.detail ? ` — ${label.detail}` : null}
            </Text>
          )
        }
      >
        <ToolInputPreview
          input={input}
          previewFields={extension?.previewFields}
        />
      </ChatInformationBox>
      <div className={classes.actions}>
        <div className={classes.buttons}>
          <Button
            variant="primary"
            size="small"
            isDisabled={actionsDisabled}
            isPending={
              pendingDecision ===
              ConversationsV2ToolConfirmationResponseDecision.Allow
            }
            onPress={() =>
              handleDecide(
                ConversationsV2ToolConfirmationResponseDecision.Allow,
              )
            }
          >
            Allow
          </Button>
          <Button
            variant="secondary"
            size="small"
            isDisabled={actionsDisabled}
            isPending={
              pendingDecision ===
              ConversationsV2ToolConfirmationResponseDecision.AllowAll
            }
            onPress={() =>
              handleDecide(
                ConversationsV2ToolConfirmationResponseDecision.AllowAll,
              )
            }
          >
            Allow all
          </Button>
          <Button
            variant="secondary"
            size="small"
            isDisabled={actionsDisabled}
            isPending={
              pendingDecision ===
              ConversationsV2ToolConfirmationResponseDecision.Deny
            }
            onPress={() =>
              handleDecide(ConversationsV2ToolConfirmationResponseDecision.Deny)
            }
          >
            Deny
          </Button>
        </div>
        {error && (
          <Text variant="body-small" color="danger">
            {error.message}
          </Text>
        )}
      </div>
    </>
  );
};

export const ToolConfirmationResponseMessage = ({
  decision,
}: {
  decision: ConversationsV2ToolConfirmationResponseDecision;
}) => {
  const classes = useStyles();
  const approved =
    decision !== ConversationsV2ToolConfirmationResponseDecision.Deny;
  return (
    <ChatInformationBox
      icon={
        <RiShieldCheckLine
          size={16}
          className={classNames({
            [classes.iconApproved]: approved,
            [classes.iconRejected]: !approved,
          })}
        />
      }
      title={approved ? 'Approved' : 'Rejected'}
    />
  );
};
